/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  Download,
  Loader2,
  MessageSquare,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@common/utils';
import { QueryTag, SearchParam, TableType } from '@common/ts';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import useTranslation from '@/hooks/useTranslation';
import { getSortedTranslatedLanguages } from '@/utils/getSortedTranslatedLanguages';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import TranscriptsColumns from './TranscriptsColumns';
import TranscriptsFilterBar from './TranscriptsFilterBar';
import { isHiddenByDefaultMetadataKey, getColumnDisplayLabel } from '@/hooks/useTranscriptsFilters';
import 'react-toastify/dist/ReactToastify.css';
import { Checkbox } from '@/components/ui/checkbox';
import {
  useMutateGenerateTranscriptsTranslation,
  useMutateDeleteConversations,
  useMutateMarkConversationsComplete,
} from '@/reactQuery/post';
import { useGetTranslationProgress } from '@/reactQuery/get';
import { useMutateDeleteTranslation } from '@/reactQuery/delete';
import getUniqueMetadataKeys, { orderMetadataColumnKeys } from '@/utils/getUniqueMetadataKeys';
import EmptyState from '@/components/ui/EmptyState';
import calculateConversationDuration from '@/utils/calculateConversationDuration';
import formatDate from '@/utils/dateFormat';
import DataTablePagination from '@/components/paginations/DataTablePagination';
import { TableData } from '@/types';
import languagesList from '@/lang/languages/languagesList';
import ConfirmTranslationModal from '@/components/Modals/ConfirmTranslationModal';
import ExportModal from '@/components/Modals/ExportModal/ExportModal';
import ConfirmDeleteConversationsModal from '@/components/Modals/ConfirmDeleteConversationsModal';
import ConfirmMarkCompleteModal from '@/components/Modals/ConfirmMarkCompleteModal';
import TranscriptsTranslationDropdown from './TranscriptsTranslationDropdown';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import FiltersSelect from '@/components/projects/single-project/filters/FiltersSelect';
import TranscriptsViewToggle from '@/components/transcriptsViewToggle/TranscriptsViewToggle';
import { TranscriptsView, EProjectStatus } from '@/enums';
import { getPageCount } from '@/utils/getPageCount';
import { getPaginationParamPage } from '@/utils/urlParams';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';

const MAX_MARK_COMPLETE_SELECTION = 100;

export default function TranscriptsTable({
  allConversations,
  projectId,
  languages,
  initialTranslatedLanguages,
  completedConversationsLength,
  showFilters = false,
  onReRun,
  rerunFilterValues = [],
  onFilterDeleteEnableRefetch,
  refetch,
  activeView = TranscriptsView.COMPLETED,
  onViewChange,
  completedCount = 0,
  incompleteCount = 0,
  screenedOutCount = 0,
  totalCount = 0,
  isLoadingConversations = false,
  showIncompleteToggle = true,
  showScreenedOutToggle = true,
  projectStatus,
  deletedConversations,
  isCompletedTranscriptsView,
  scrollToConversation,
  onScrollTo,
  // URL table props received from parent
  urlCurrentPage,
  updateUrlPage,
  urlSorting,
  updateUrlSorting,
}: TableData) {
  const languageListwithOriginal = languagesList.language.map(({ label }) => label);
  const { setTranslatingProjects, translatingProjects } = useTranslationsLoadingStore();
  const { lang } = useTranslation();
  const { isUserEditor, isUserOwner } = useWorkspaceRoles();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMarkCompleteModalOpen, setIsMarkCompleteModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasTranslationPagination = !!searchParams.get(
    getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS),
  );
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const activeLanguage = translationLanguage || initialTranslatedLanguages[0];
  const [dropdownLanguage, setDropdownLanguage] = useState<string | null>(null);
  const [translatedLanguages, setTranslatedLanguages] = useState<string[]>([]);
  const [unTranslatedLanguages, setUntranslatedLanguages] = useState<string[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [pendingMarkCompleteIds, setPendingMarkCompleteIds] = useState<string[]>([]);
  const isProjectPublished = projectStatus === EProjectStatus.PUBLISHED;
  const isScreenedOutView = activeView === TranscriptsView.SCREENED_OUT;
  const canDeleteConversation =
    (isUserEditor || isUserOwner) &&
    isProjectPublished &&
    showIncompleteToggle &&
    !isScreenedOutView;
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const savedScrollLeft = useRef(0);

  const queryClient = useQueryClient();
  const { mutateAsync } = useMutateGenerateTranscriptsTranslation(projectId);
  const { mutateAsync: deleteTranslation } = useMutateDeleteTranslation(projectId);
  const { mutateAsync: deleteConversations, isPending: isDeletingConversations } =
    useMutateDeleteConversations();
  const { mutateAsync: markConversationsComplete, isPending: isMarkingComplete } =
    useMutateMarkConversationsComplete();

  const uniqueMetadataKeys = useMemo(() => {
    if (allConversations) {
      return getUniqueMetadataKeys(allConversations);
    }
    return new Set<string>();
  }, [allConversations]);

  const currentProjectIsTranslating = translatingProjects?.find(
    (p) => p.projectId === projectId && p.source === 'transcripts',
  );
  const isTranslating = !!currentProjectIsTranslating;
  const pendingLanguage = currentProjectIsTranslating?.language || null;

  // Poll translation progress via Tanstack Query (auto-enabled when pendingLanguage is set)
  const { data: progressData, dataUpdatedAt: progressUpdatedAt } = useGetTranslationProgress(
    projectId,
    pendingLanguage,
  );
  const isCompletingRef = useRef(false);
  const lastCountRef = useRef<number>(0);
  const stableCountRef = useRef<number>(0);
  const translationStartTimeRef = useRef<number>(0);
  const STABLE_POLLS_THRESHOLD = 3; // 3 consecutive polls at same value (~15s) = complete
  const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes max

  // TELV1-583 — sorted so the column order is stable. Previously this was a
  // bare `Array.from(...)`, which inherited the Set's first-seen-row order,
  // so changing the table sort silently rearranged the metadata columns.
  // Memoised because it is the dependency of the effect below.
  const metadataKeysArray = useMemo(
    () => orderMetadataColumnKeys(uniqueMetadataKeys),
    [uniqueMetadataKeys],
  );

  // Default-hide metadata columns that are Tellet-internal or panel-integration
  // boilerplate. User can still toggle them back on via the View menu; we only
  // seed defaults once per key so user preferences aren't overwritten.
  const appliedHiddenDefaultsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const toHide: VisibilityState = {};
    metadataKeysArray.forEach((key) => {
      if (appliedHiddenDefaultsRef.current.has(key)) return;
      appliedHiddenDefaultsRef.current.add(key);
      if (isHiddenByDefaultMetadataKey(key)) toHide[`metadata.${key}`] = false;
    });
    if (Object.keys(toHide).length > 0) {
      // Defaults first, user toggles (prev) win on collision.
      setColumnVisibility((prev) => ({ ...toHide, ...prev }));
    }
  }, [metadataKeysArray]);

  const columns = TranscriptsColumns(metadataKeysArray, activeView);

  const conversationsWithDuration = useMemo(
    () =>
      allConversations.map((conversation) => ({
        ...conversation,
        // Use startedAt if available, otherwise fall back to createdAt (for screened out conversations that never started)
        createdAt: formatDate(
          conversation.startedAt || conversation.createdAt,
          'DD/MMM/YYYY - hh:mm:ss A',
        ),
        duration: calculateConversationDuration({
          startedAt: conversation.startedAt,
          finishedAt: conversation.finishedAt,
        }),
      })),
    [allConversations],
  );

  const handleLanguageSelection = async (language: string) => {
    // Prevent multiple simultaneous translations
    if (isTranslating && currentProjectIsTranslating) {
      toast.warning(
        `Translation to ${currentProjectIsTranslating.language} is already in progress. Please wait.`,
      );
      return;
    }

    searchParams.delete(SearchParam.SELECTED_TRANSCRIPT);
    if (hasTranslationPagination) {
      searchParams.delete(getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS));
    }
    if (translatedLanguages?.includes(language) || language === initialTranslatedLanguages[0]) {
      if (language === initialTranslatedLanguages[0]) {
        searchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
      } else {
        searchParams.set(SearchParam.TRANSLATION_LANGUAGE, language);
      }
      setSearchParams(searchParams);
    } else {
      setDropdownLanguage(language);
      setIsModalOpen(true);
    }
  };

  const confirmTranslation = async () => {
    if (!dropdownLanguage || !completedConversationsLength) return;
    setIsModalOpen(false);
    setSearchParams({ [SearchParam.TRANSLATION_LANGUAGE]: dropdownLanguage });
    setTranslatingProjects((prev) => [
      ...prev,
      {
        projectId,
        language: dropdownLanguage,
        length: completedConversationsLength,
        source: 'transcripts',
      },
    ]);
    try {
      await mutateAsync({ languages: [dropdownLanguage] });
      toast.info(`Translation to ${dropdownLanguage} initiated. You'll be notified when complete.`);
    } catch (translationError) {
      const errorMessage = getErrorMessage(translationError);
      toast.error(`Translation failed: ${errorMessage}`);
      setTranslatingProjects((prev) =>
        prev.filter((p) => !(p.projectId === projectId && p.source === 'transcripts')),
      );
    }
  };

  // Handle language deletion
  const handleDeleteLanguage = async (language: string) => {
    try {
      toast.info(`Deleting ${language} translations...`);
      await deleteTranslation({ language });
      toast.success(`${language} translations deleted successfully`);

      // If we were viewing the deleted language, switch back to original
      if (activeLanguage === language) {
        searchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
        setSearchParams(searchParams);
      }
    } catch (deletionError) {
      const errorMessage = getErrorMessage(deletionError);
      toast.error(`Failed to delete ${language} translations: ${errorMessage}`);
    }
  };

  // Calculate page count using totalCount
  const PAGE_SIZE = 20;
  const pageCount = getPageCount(totalCount, PAGE_SIZE);

  const table = useReactTable({
    data: conversationsWithDuration || [],
    columns,
    pageCount,
    onSortingChange: updateUrlSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualSorting: true,
    state: {
      sorting: urlSorting,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: urlCurrentPage - 1,
        pageSize: PAGE_SIZE,
      },
    },
  });

  useEffect(() => {
    setTranslatedLanguages(initialTranslatedLanguages);
    setUntranslatedLanguages(
      languageListwithOriginal.filter(
        (language) => !initialTranslatedLanguages?.includes(language),
      ),
    );
  }, [initialTranslatedLanguages]);

  // Restore horizontal scroll position after data changes (e.g. sort/pagination)
  useEffect(() => {
    if (tableScrollRef.current && savedScrollLeft.current) {
      tableScrollRef.current.scrollLeft = savedScrollLeft.current;
    }
  }, [conversationsWithDuration]);

  // Track translation start time and reset stability counters when translation begins/ends
  useEffect(() => {
    if (isTranslating && !translationStartTimeRef.current) {
      translationStartTimeRef.current = Date.now();
      lastCountRef.current = 0;
      stableCountRef.current = 0;
    }
    if (!isTranslating) {
      translationStartTimeRef.current = 0;
    }
  }, [isTranslating]);

  // React to progress changes — use stability detection instead of expected total comparison.
  // We complete when the translated count is greater than zero and has not changed for
  // STABLE_POLLS_THRESHOLD consecutive polls, or when the 5-minute timeout elapses.
  useEffect(() => {
    if (!pendingLanguage || !isTranslating || !progressData || isCompletingRef.current) return;

    const translatedCount = progressData.count;

    // Timeout check — abort if translation is taking too long
    const elapsed = Date.now() - translationStartTimeRef.current;
    if (elapsed >= TIMEOUT_MS) {
      setTranslatingProjects((prev) =>
        prev.filter((p) => !(p.projectId === projectId && p.source === 'transcripts')),
      );
      toast.error(
        `Translation to ${pendingLanguage} is taking longer than expected. Please try again or contact support if the issue persists.`,
      );
      return;
    }

    // TELV1-501 — partial retries (coverage banner) start with a high,
    // already-stable project-wide count. Completion additionally requires
    // the count to have EXCEEDED the retry's baseline, otherwise the
    // plateau-from-start reads as instant success while the retried
    // conversations are still translating. Full translations carry no
    // baseline (undefined → -1 → always passed), keeping the original
    // behavior. A retry where nothing succeeds ends via the timeout
    // (accurate: nothing completed).
    const baselineCount = currentProjectIsTranslating?.baselineCount ?? -1;

    // Stability detection: count > 0 and unchanged for STABLE_POLLS_THRESHOLD consecutive polls
    if (translatedCount > 0 && translatedCount === lastCountRef.current) {
      // eslint-disable-next-line no-plusplus
      stableCountRef.current++;
    } else {
      stableCountRef.current = 0;
    }
    lastCountRef.current = translatedCount;

    if (
      translatedCount > 0 &&
      translatedCount > baselineCount &&
      stableCountRef.current >= STABLE_POLLS_THRESHOLD
    ) {
      isCompletingRef.current = true;

      const completeTranslation = async () => {
        try {
          // Refetch data BEFORE clearing loading state
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [QueryTag.CONVERSATIONS] }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_LANGUAGES(projectId) }),
            // Invalidate per-question transcripts for cross-tab consistency (ResponsesPanel / ResponsesZone)
            queryClient.invalidateQueries({ queryKey: [QueryTag.QUESTION_TRANSCRIPTS] }),
          ]);

          toast.success(`Translation to ${pendingLanguage} completed!`);
        } finally {
          // Clear state in finally so spinner clears even if invalidateQueries rejects
          setTranslatingProjects((prev) =>
            prev.filter((p) => !(p.projectId === projectId && p.source === 'transcripts')),
          );
          isCompletingRef.current = false;
        }
      };

      completeTranslation();
    }
    // progressUpdatedAt changes on every successful refetch (even when data is structurally
    // identical), ensuring this effect re-fires despite TanStack Query's structural sharing.
  }, [progressData, progressUpdatedAt, pendingLanguage, isTranslating]);

  useEffect(() => {
    if (languages?.length && !isTranslating) {
      setTranslatedLanguages((prev) => {
        const newLanguages = languages.filter((language) => !prev?.includes(language));
        return [...prev, ...newLanguages];
      });

      setUntranslatedLanguages(
        languagesList.language
          .map(({ label }) => label)
          .filter((language) => !languages?.includes(language) && language !== activeLanguage),
      );
    }
  }, [languages, activeLanguage, isTranslating, projectId]);

  const selectedConversationIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const selectedCount = selectedConversationIds.length;

  const handleDeleteConversations = async () => {
    if (selectedCount === 0) return;

    // Close modal immediately and clear selection
    setIsDeleteModalOpen(false);
    setRowSelection({});

    // Track which conversations are pending deletion
    setPendingDeleteIds(selectedConversationIds);

    try {
      await deleteConversations({
        project_id: projectId,
        conversationIds: selectedConversationIds,
      });

      const message = `${
        selectedCount === 1 ? 'Conversation' : 'Conversations'
      } deleted successfully`;

      toast.success(message);
    } catch (error: any) {
      const fallbackMessage = `An error occurred and ${
        selectedCount === 1 ? 'conversation' : 'conversations'
      } could not be deleted`;

      const errorMessage = getErrorMessage(error, fallbackMessage);
      toast.error(errorMessage);

      // Clear pending deletes on error
      setPendingDeleteIds([]);
    } finally {
      // Invalidate conversations cache to trigger a fresh fetch
      await queryClient.invalidateQueries({
        queryKey: [QueryTag.CONVERSATIONS],
      });
      // Also trigger the original refetch for the run state
      if (refetch) {
        refetch();
      }
    }
  };

  const handleDeleteButtonClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleMarkConversationsComplete = async () => {
    if (selectedCount === 0) return;

    // Close modal immediately and clear selection
    setIsMarkCompleteModalOpen(false);
    setRowSelection({});

    // Track which conversations are pending
    setPendingMarkCompleteIds(selectedConversationIds);

    try {
      const result = await markConversationsComplete({
        projectId,
        conversationIds: selectedConversationIds,
      });

      const markedCount = result?.data?.markedCount || selectedCount;
      toast.success(`${markedCount} ${lang.get('msg.conversationsMarkedComplete')}`);
    } catch (error: any) {
      const fallbackMessage = `An error occurred and ${
        selectedCount === 1 ? 'conversation' : 'conversations'
      } could not be marked as complete`;

      const errorMessage = getErrorMessage(error, fallbackMessage);
      toast.error(errorMessage);

      // Clear pending on error
      setPendingMarkCompleteIds([]);
    } finally {
      // Clear pending IDs immediately — conversations with DONE status still appear
      // in the incomplete view, so the data-driven cleanup effect won't clear them
      setPendingMarkCompleteIds([]);

      // Invalidate conversations cache to trigger a fresh fetch
      await queryClient.invalidateQueries({
        queryKey: [QueryTag.CONVERSATIONS],
      });
      // Also trigger the original refetch for the run state
      if (refetch) {
        refetch();
      }
    }
  };

  const handleMarkCompleteButtonClick = () => {
    setIsMarkCompleteModalOpen(true);
  };

  const handleSelectAll = useCallback(() => {
    if (selectedCount === conversationsWithDuration?.length) {
      // Deselect all
      setRowSelection({});
    } else {
      // Select all
      const allSelection: Record<string, boolean> = {};
      conversationsWithDuration?.forEach((conversation) => {
        const rowId = conversation.conversation_id || conversation._id;
        allSelection[rowId] = true;
      });
      setRowSelection(allSelection);
    }
  }, [selectedCount, conversationsWithDuration]);

  const isAllSelected =
    conversationsWithDuration?.length > 0 && selectedCount === conversationsWithDuration?.length;

  // Clear selection when switching between completed/incomplete tabs
  useEffect(() => {
    setRowSelection({});
  }, [activeView]);

  useEffect(() => {
    if (scrollToConversation && rowRefs.current[scrollToConversation]) {
      rowRefs.current[scrollToConversation]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      onScrollTo(null);
    }
    // onScrollTo is included to satisfy exhaustive-deps and guard
    // against the parent ever passing a non-stable callback (today it
    // passes a useState setter, which is stable). Without the dep, a
    // future refactor that changes onScrollTo's identity would silently
    // capture a stale callback inside this closure.
  }, [scrollToConversation, onScrollTo]);

  // Clear pending deletes when conversations are actually removed from the data
  useEffect(() => {
    if (pendingDeleteIds.length > 0 && allConversations) {
      const currentConversationIds = allConversations.map(
        (conv) => conv.conversation_id || conv._id,
      );

      const stillPendingIds = pendingDeleteIds.filter((id) => currentConversationIds.includes(id));

      if (stillPendingIds.length < pendingDeleteIds.length) {
        setPendingDeleteIds(stillPendingIds);
      }
    }
  }, [allConversations, pendingDeleteIds]);

  return (
    <>
      <div className="w-full">
        <div className="flex flex-wrap items-center w-full pb-2 border-b border-neutral-100">
          <div className="flex items-center justify-between w-full gap-4 mb-2">
            {/* Left group — Filters + View toggle */}
            <div className="flex items-center gap-2">
              {showFilters && onReRun && onFilterDeleteEnableRefetch && refetch && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-neutral-400 uppercase tracking-wide">
                    {lang.get('msg.analysisScope') || 'Analysis scope'}
                  </span>
                  <FiltersSelect
                    onReRun={onReRun}
                    rerunFilterValues={rerunFilterValues}
                    onFilterDeleteEnableRefetch={onFilterDeleteEnableRefetch}
                    refetch={refetch}
                  />
                </div>
              )}

              {showIncompleteToggle && onViewChange && (
                <TranscriptsViewToggle
                  activeView={activeView}
                  onViewChange={onViewChange}
                  completedCount={completedCount}
                  incompleteCount={incompleteCount}
                  screenedOutCount={screenedOutCount}
                  isLoading={isLoadingConversations}
                  showIncompleteToggle={showIncompleteToggle}
                  showScreenedOutToggle={showScreenedOutToggle}
                />
              )}

              {!!deletedConversations && (
                <span className="text-xs text-neutral-400 flex items-center gap-1 ml-1">
                  <Trash2 className="h-3.5 w-3.5" />
                  {deletedConversations} {lang.get('msg.deleted').toLowerCase()}
                </span>
              )}
            </div>

            {/* Center — Selection actions (visible only when rows are selected) */}
            {canDeleteConversation && selectedCount > 0 && (
              <div className="flex items-center gap-2 bg-neutral-50 border rounded-lg px-3 py-1.5">
                <span className="text-sm text-neutral-600 font-medium">
                  {selectedCount} {lang.get('msg.selected')}
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteButtonClick}
                  disabled={isDeletingConversations}
                  className="flex items-center gap-1.5"
                >
                  {isDeletingConversations && <Loader2 className="animate-spin h-4 w-4" />}
                  <Trash2 className="h-4 w-4" />
                  {lang.get('msg.delete')}
                </Button>
                {activeView === TranscriptsView.INCOMPLETE && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleMarkCompleteButtonClick}
                    disabled={isMarkingComplete || selectedCount > MAX_MARK_COMPLETE_SELECTION}
                    className="flex items-center gap-1.5"
                  >
                    {isMarkingComplete ? (
                      <Loader2 className="animate-spin h-4 w-4" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    {lang.get('msg.markAsComplete')}
                  </Button>
                )}
              </div>
            )}

            {/* Right group — Language, Export, Column visibility */}
            <div className="flex items-center gap-2">
              {!!completedConversationsLength && isCompletedTranscriptsView && (
                <TranscriptsTranslationDropdown
                  isLoading={!!currentProjectIsTranslating}
                  activeLanguage={activeLanguage! || dropdownLanguage}
                  translatedLanguages={getSortedTranslatedLanguages(
                    initialTranslatedLanguages,
                    translatedLanguages,
                  )}
                  unTranslatedLanguages={unTranslatedLanguages}
                  handleLanguageSelection={handleLanguageSelection}
                  onDeleteLanguage={handleDeleteLanguage}
                  pendingLanguage={currentProjectIsTranslating?.language || null}
                />
              )}

              {/*
                TELV1-252: Export button is always visible regardless of the
                currently-selected transcript language. The CSV export emits
                one column per question per language (see
                export.file.service.ts:createQuestionsAsColumnsExport), so
                the user always gets the full multilingual dataset; gating
                on translationLanguage just hid the only path to that data
                while in a translated view.
              */}
              <Button
                variant="default"
                onClick={() => setIsExportModalOpen(true)}
                className="flex items-center gap-1"
              >
                <Download className="h-4 w-4" />
                {lang.get('msg.export')}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="ml-auto text-dark-text">
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    {lang.get('msg.view')}
                    <ChevronDown className="w-4 h-4 ml-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column?.id}
                        checked={column.getIsVisible()}
                        // Radix closes the menu by default on item click. For a
                        // multi-toggle list like column visibility, users expect
                        // to flip several checkboxes in a row without re-opening
                        // the menu. preventDefault on the select event keeps the
                        // menu open while still firing onCheckedChange.
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {getColumnDisplayLabel(column.id)}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        {!currentProjectIsTranslating &&
        !isDeletingConversations &&
        !isMarkingComplete &&
        pendingDeleteIds.length === 0 &&
        pendingMarkCompleteIds.length === 0 ? (
          <>
            <TranscriptsFilterBar visibleMetadataKeys={metadataKeysArray} activeView={activeView} />
            <div
              ref={tableScrollRef}
              onScroll={(e) => {
                savedScrollLeft.current = e.currentTarget.scrollLeft;
              }}
              className="border rounded-md text-dark-text overflow-x-auto"
            >
              <Table>
                <TableHeader>
                  {table?.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup?.id}>
                      {canDeleteConversation && table?.getRowModel().rows?.length > 0 && (
                        <TableHead>
                          <Checkbox
                            className="cursor-pointer"
                            checked={isAllSelected}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                      )}
                      {headerGroup.headers.map((header) => {
                        const canSort = header.column.getCanSort();
                        const sortDirection = header.column.getIsSorted();

                        return (
                          <TableHead key={header?.id}>
                            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                            <div
                              className={`flex items-center w-full gap-2${
                                canSort ? ' group cursor-pointer' : ''
                              }`}
                              onClick={
                                canSort
                                  ? () =>
                                      header.column.toggleSorting(
                                        header.column.getIsSorted() === 'asc',
                                      )
                                  : undefined
                              }
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                              {canSort && (
                                <div>
                                  {/* eslint-disable-next-line no-nested-ternary */}
                                  {sortDirection === 'asc' ? (
                                    <ArrowUp className="h-4 w-4" />
                                  ) : sortDirection === 'desc' ? (
                                    <ArrowDown className="h-4 w-4" />
                                  ) : (
                                    <ArrowUpDown className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  )}
                                </div>
                              )}
                            </div>
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table?.getRowModel().rows?.length || currentProjectIsTranslating ? (
                    table?.getRowModel().rows.map((row) => {
                      const rowId = row.original?.conversation_id || row.original?._id;
                      return (
                        <TableRow
                          key={rowId}
                          className={!isScreenedOutView ? 'cursor-pointer' : undefined}
                          data-state={row.getIsSelected() && 'selected'}
                          ref={(el) => {
                            rowRefs.current[rowId] = el;
                          }}
                        >
                          {canDeleteConversation && (
                            <TableCell className="py-0 h-[60px]">
                              <Checkbox
                                className="cursor-pointer"
                                checked={!!rowSelection[rowId]}
                                onCheckedChange={() => {
                                  setRowSelection((prev) => ({
                                    ...prev,
                                    [rowId]: !prev[rowId],
                                  }));
                                }}
                              />
                            </TableCell>
                          )}
                          {row.getVisibleCells().map((cell) => (
                            <TableCell className="py-0 h-[60px]" key={cell?.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={(columns?.length ?? 0) + (canDeleteConversation ? 1 : 0)}
                        className="h-48"
                      >
                        <EmptyState
                          icon={<MessageSquare className="h-10 w-10" />}
                          title={lang.get('msg.noResults')}
                          description={lang.get('msg.noConversationsMatchCurrentView')}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-80">
            <Loader2 className="animate-spin h-8 w-8" />
          </div>
        )}
        <DataTablePagination
          table={table}
          currentPage={urlCurrentPage}
          onPageChange={updateUrlPage}
          totalCount={totalCount}
        />
      </div>
      <ConfirmTranslationModal
        isOpen={isModalOpen}
        onConfirm={confirmTranslation}
        onCancel={() => {
          setDropdownLanguage(activeLanguage);
          setIsModalOpen(false);
        }}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        projectId={projectId!}
        transcriptsView={activeView}
      />
      <ConfirmDeleteConversationsModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConversations}
        conversationCount={selectedCount}
        activeView={activeView}
        isLoading={isDeletingConversations}
      />
      <ConfirmMarkCompleteModal
        isOpen={isMarkCompleteModalOpen}
        onClose={() => setIsMarkCompleteModalOpen(false)}
        onConfirm={handleMarkConversationsComplete}
        conversationCount={selectedCount}
        hasInProgressConversations={allConversations
          .filter((conv) => selectedConversationIds.includes(conv.conversation_id || conv._id))
          .some((conv) => conv.status === 'in_progress')}
        isLoading={isMarkingComplete}
        maxSelectionExceeded={selectedCount > MAX_MARK_COMPLETE_SELECTION}
      />
    </>
  );
}
